import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { bookingService, PaymentMethod } from '@/services/bookingService';
import { Calendar, Users, CreditCard, MapPin, Percent, User } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import SaudiRiyal from '@/components/shared/saudi-riyal';

interface BookingFormProps {
  propertyId?: number;
  onSuccess?: (booking: any) => void;
  onCancel?: () => void;
}

export function BookingForm({ propertyId, onSuccess, onCancel }: BookingFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // User type toggle state
  const [isNewUser, setIsNewUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserPhone, setNewUserPhone] = useState('');
  
  // User and property selection states
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(propertyId?.toString() || '');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{id: string, name: string}>>([]);
  const [availableProperties, setAvailableProperties] = useState<Array<{id: string, name: string}>>([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<PaymentMethod[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodDropdownOpen, setPaymentMethodDropdownOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    userId: '',
    property_id: propertyId || 0,
    startDate: '',
    endDate: '',
    check_in: '',
    check_out: '',
    guest_count: 1,
    payment_method: '',
    provider_name: '',
    couponCode: '',
    originalAmount: 0,
    discountAmount: 0,
    finalAmount: 0,
    total_price: '0.00',
    deposit_amount: '0.00',
    deposit_paid: '0.00',
    remaining_amount: '0.00',
    status: 'confirmed',
    payment_status: 'pending',
    payment_reference: null,
    special_requests: '',
    notes: 'Created by admin'
  });

  const [paymentSummary, setPaymentSummary] = useState({
    total_amount: 0,
    deposit_amount: 0,
    remaining_amount: 0,
    coupon_discount: 0
  });

  // Load users for dropdown
  const loadUsers = async (searchQuery: string = '') => {
    try {
      setUsersLoading(true);
      const users = await bookingService.getUsers(searchQuery);
      
      if (Array.isArray(users)) {
        setAvailableUsers(users);
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setAvailableUsers([]);
      toast({
        title: t('error') || 'خطأ',
        description: t('failedToLoadUsers') || 'فشل في تحميل المستخدمين',
        variant: 'destructive'
      });
    } finally {
      setUsersLoading(false);
    }
  };

  // Load properties for dropdown
  const loadProperties = async (searchQuery: string = '') => {
    try {
      setPropertiesLoading(true);
      const properties = await bookingService.getProperties(searchQuery);
      
      if (Array.isArray(properties)) {
        setAvailableProperties(properties);
      } else {
        setAvailableProperties([]);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
      setAvailableProperties([]);
      toast({
        title: t('error') || 'خطأ',
        description: t('failedToLoadProperties') || 'فشل في تحميل العقارات',
        variant: 'destructive'
      });
    } finally {
      setPropertiesLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    try {
      const response = await bookingService.getPaymentMethods();
      console.log('💳 Payment methods response:', response);
      
      if (response.success && response.data && Array.isArray(response.data.payment_methods)) {
        console.log('💳 Setting payment methods:', response.data.payment_methods);
        setAvailablePaymentMethods(response.data.payment_methods);
      } else {
        console.log('💳 No valid payment methods data, setting empty array');
        setAvailablePaymentMethods([]);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      // Fallback to empty array on error
      const mockPaymentMethods: PaymentMethod[] = [];
      console.log('💳 Using mock payment methods:', mockPaymentMethods);
      setAvailablePaymentMethods(mockPaymentMethods);
      
      toast({
        title: t('warning') || 'تحذير',
        description: t('usingMockPaymentMethods') || 'استخدام طرق دفع تجريبية',
        variant: 'default'
      });
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadAllData = async () => {
      try {
        await Promise.all([
          loadUsers(),
          loadProperties(),
          loadPaymentMethods()
        ]);
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };
    
    loadAllData();
  }, []);

  // Calculate payment summary when form data changes
  useEffect(() => {
    if (formData.check_in && formData.check_out && selectedUserId && selectedPropertyId) {
      calculatePaymentSummary();
    }
  }, [formData.check_in, formData.check_out, formData.guest_count, formData.couponCode, selectedUserId, selectedPropertyId]);

  const calculatePaymentSummary = () => {
    if (!formData.check_in || !formData.check_out) return;
    
    // Calculate number of nights
    const checkIn = new Date(formData.check_in);
    const checkOut = new Date(formData.check_out);
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
    
    if (nights <= 0) return;
    
    // Base price per night (SAR 500 per night, adjustable)
    const basePricePerNight = 500;
    const totalAmount = nights * basePricePerNight * formData.guest_count;
    
    // Apply coupon discount
    let couponDiscount = 0;
    // if (formData.couponCode && formData.couponCode.toUpperCase() === ) {
    //   couponDiscount = totalAmount * 0.2; // 20% discount
    // }
    
    const finalAmount = totalAmount - couponDiscount;
    const depositAmount = 0; // No deposit required
    const remainingAmount = finalAmount;
    
    // Update form data with calculated values
    setFormData(prev => ({
      ...prev,
      startDate: prev.check_in,
      endDate: prev.check_out,
      originalAmount: totalAmount,
      discountAmount: couponDiscount,
      finalAmount: finalAmount,
      total_price: finalAmount.toFixed(2),
      deposit_amount: '0.00',
      deposit_paid: '0.00',
      remaining_amount: finalAmount.toFixed(2),
      userId: selectedUserId,
      property_id: parseInt(selectedPropertyId) || 0
    }));
    
    setPaymentSummary({
      total_amount: finalAmount,
      deposit_amount: 0,
      remaining_amount: finalAmount,
      coupon_discount: couponDiscount
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form fields
    if (!selectedPropertyId || !formData.check_in || !formData.check_out) {
      toast({
        title: t('error'),
        description: t('pleaseFillRequiredFields') || 'يرجى ملء جميع الحقول المطلوبة',
        variant: 'destructive'
      });
      return;
    }

    // Validate user selection based on type
    let userId = selectedUserId;
    if (isNewUser) {
      if (!newUserPhone.trim()) {
        toast({
          title: t('error'),
          description: t('phoneRequiredWhenNoUser') || 'رقم الهاتف مطلوب عند عدم اختيار مستخدم',
          variant: 'destructive'
        });
        return;
      }
      // For new users, we do NOT create the user here.
      // Backend will find or create a minimal user record using phone/customerName.
      userId = ''; // Ensure we don't send userId for new users
    } else {
      if (!selectedUserId) {
        toast({
          title: t('error'),
          description: t('pleaseSelectUser') || 'يرجى اختيار مستخدم',
          variant: 'destructive'
        });
        return;
      }
    }

    // Validate payment method
    if (!formData.payment_method) {
      toast({
        title: t('error'),
        description: t('pleaseSelectPaymentMethod') || 'يرجى اختيار طريقة الدفع',
        variant: 'destructive'
      });
      return;
    }

    // Get payment method code from selected payment method
    const selectedPaymentMethod = availablePaymentMethods.find(
      (method) => method.provider_name === formData.payment_method
    );
    const paymentMethodCode = selectedPaymentMethod?.provider_code || formData.payment_method;

    try {
      setLoading(true);
      
      // Prepare common data for /admin/bookings
      const checkInDate = new Date(formData.check_in + 'T15:00:00Z').toISOString();
      const checkOutDate = new Date(formData.check_out + 'T12:00:00Z').toISOString();
      
      const baseBookingData: any = {
        property_id: parseInt(selectedPropertyId),
        check_in: checkInDate,
        check_out: checkOutDate,
        guest_count: formData.guest_count,
        payment_method_code: paymentMethodCode,
        payment_scope: 'deposit'
      };
      
      // Only include optional fields if they have values
      if (formData.special_requests && formData.special_requests.trim()) {
        baseBookingData.special_requests = formData.special_requests.trim();
      }
      
      if (formData.couponCode && formData.couponCode.trim()) {
        baseBookingData.couponCode = formData.couponCode.trim();
      }

      // Build payload based on user type
      let bookingData: any;
      if (isNewUser) {
        bookingData = {
          ...baseBookingData,
          phone: newUserPhone.trim(),
          customerName: newUserName.trim() || undefined
        };
      } else {
        bookingData = {
          ...baseBookingData,
          userId: userId
        };
      }
      
      console.log('📝 Submitting admin booking:', bookingData);
      
      const response = await bookingService.createAdminBooking(bookingData);
      
      if (response.success && response.data) {
        const data: any = response.data;
        const smsSent = data.sms_sent;
        const smsPhone = data.phone;

        // Show success message with SMS status
        if (smsSent && smsPhone) {
          toast({
            title: t('success') || 'نجح',
            description: `${t('smsSentToCustomerCompletePayment') || 'تم إرسال رسالة للعميل، يرجى إكمال عملية الدفع'} - ${t('smsSentTo') || 'تم إرسال رسالة نصية إلى'} ${smsPhone}`
          });
        } else {
           
          if (smsSent === false) {
            toast({
              title: t('warning') || 'تحذير',
              description: t('smsNotSent') || 'لم يتم إرسال الرسالة النصية. يرجى التحقق من إعدادات SMS',
              variant: 'default'
            });
          }
        }
        
        onSuccess?.(data);
      } else {
        // Map known backend error codes to friendly messages
        if (response.message === 'MISSING_REQUIRED_FIELDS') {
          toast({
            title: t('error') || 'خطأ',
            description: t('missingRequiredFields') || 'بعض الحقول المطلوبة مفقودة. يرجى التحقق من البيانات.',
            variant: 'destructive'
          });
        } else if (response.message === 'USER_NOT_FOUND') {
          toast({
            title: t('error') || 'خطأ',
            description: t('userNotFound') || 'المستخدم المحدد غير موجود.',
            variant: 'destructive'
          });
        } else {
          throw new Error(response.message || t('failedToCreateBooking') || 'فشل في إنشاء الحجز');
        }
      }
    } catch (error: any) {
      console.error('Failed to create booking:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = t('failedToCreateBooking') || 'فشل في إنشاء الحجز';
      
      // Handle 422 validation errors
      if (error.response?.status === 422) {
        const errorData = error.response.data;
        
        // Extract validation errors
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          const validationErrors = errorData.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            if (err.field && err.message) return `${err.field}: ${err.message}`;
            return err.msg || err.message || JSON.stringify(err);
          });
          errorMessage = validationErrors.join(', ');
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: t('error') || 'خطأ',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  const getMaxDate = () => {
    return format(addDays(new Date(), 365), 'yyyy-MM-dd');
  };

  return (
    <div className="w-full space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6 w-full">
            {/* User Type Toggle */}
            <div className="flex items-center space-x-2 space-x-reverse p-4 border rounded-lg bg-muted/50">
              <Checkbox
                id="isNewUser"
                checked={isNewUser}
                onCheckedChange={(checked) => {
                  setIsNewUser(checked as boolean);
                  if (checked) {
                    setSelectedUserId('');
                  } else {
                    setNewUserName('');
                    setNewUserPhone('');
                  }
                }}
              />
              <Label
                htmlFor="isNewUser"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                {t('newUser') || 'مستخدم جديد'}
              </Label>
            </div>

            {/* Form Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Selection - Conditional based on isNewUser */}
              {!isNewUser ? (
                <div className="space-y-2">
                  <Label>{t('selectUser') || 'اختر المستخدم'} *</Label>
                  <Popover open={userDropdownOpen} onOpenChange={setUserDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={userDropdownOpen}
                        className="w-full justify-between"
                      >
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          {selectedUserId
                            ? availableUsers?.find((user) => user.id === selectedUserId)?.name
                            : (t('selectUser') || 'اختر المستخدم')}
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput 
                          placeholder={t('searchUsers') || 'البحث عن المستخدمين'} 
                          onValueChange={loadUsers}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {usersLoading ? (t('loading') || 'جاري التحميل...') : (t('noUsersFound') || 'لم يتم العثور على مستخدمين')}
                          </CommandEmpty>
                          <CommandGroup>
                            {Array.isArray(availableUsers) && availableUsers.map((user) => (
                              <CommandItem
                                key={user.id}
                                value={user.id}
                                onSelect={(currentValue) => {
                                  setSelectedUserId(currentValue === selectedUserId ? '' : currentValue);
                                  setUserDropdownOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedUserId === user.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <span className="font-medium">{user.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="newUserName">{t('userName') || 'اسم المستخدم'}</Label>
                    <Input
                      id="newUserName"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      placeholder={t('enterUserName') || 'أدخل اسم المستخدم'}
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newUserPhone">{t('userPhone') || 'رقم الهاتف'} *</Label>
                    <Input
                      id="newUserPhone"
                      value={newUserPhone}
                      onChange={(e) => setNewUserPhone(e.target.value)}
                      placeholder={t('enterUserPhone') || '+966XXXXXXXXX'}
                      disabled={loading}
                    />
                  </div>
                </>
              )}

              {/* Property Selection */}
              <div className="space-y-2">
                <Label>{t('selectProperty') || 'اختر العقار'} *</Label>
              <Popover open={propertyDropdownOpen} onOpenChange={setPropertyDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={propertyDropdownOpen}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      {selectedPropertyId
                        ? availableProperties?.find((property) => property.id === selectedPropertyId)?.name
                        : (t('selectProperty') || 'اختر العقار')}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput 
                      placeholder={t('searchProperties') || 'البحث عن العقارات'} 
                      onValueChange={loadProperties}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {propertiesLoading ? (t('loading') || 'جاري التحميل...') : (t('noPropertiesFound') || 'لم يتم العثور على عقارات')}
                      </CommandEmpty>
                      <CommandGroup>
                        {Array.isArray(availableProperties) && availableProperties.map((property) => (
                          <CommandItem
                            key={property.id}
                            value={property.id}
                            onSelect={(currentValue) => {
                              setSelectedPropertyId(currentValue === selectedPropertyId ? '' : currentValue);
                              setPropertyDropdownOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedPropertyId === property.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="font-medium">{property.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

              {/* Check-in Date */}
              <div className="space-y-2">
                <Label htmlFor="check_in">{t('checkInDate')} *</Label>
                <Input
                  id="check_in"
                  type="date"
                  value={formData.check_in}
                  onChange={(e) => handleInputChange('check_in', e.target.value)}
                  min={getMinDate()}
                  max={getMaxDate()}
                  required
                />
              </div>

              {/* Check-out Date */}
              <div className="space-y-2">
                <Label htmlFor="check_out">{t('checkOutDate')} *</Label>
                <Input
                  id="check_out"
                  type="date"
                  value={formData.check_out}
                  onChange={(e) => handleInputChange('check_out', e.target.value)}
                  min={formData.check_in || getMinDate()}
                  max={getMaxDate()}
                  required
                />
              </div>
            </div>

            {/* Second Row - Guest Count, Payment Method, Coupon */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Guest Count */}
              <div className="space-y-2">
                <Label htmlFor="guest_count">{t('guestCount')} *</Label>
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <Select
                    value={formData.guest_count.toString()}
                    onValueChange={(value) => handleInputChange('guest_count', parseInt(value))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label>{t('paymentMethod')} *</Label>
                <Popover open={paymentMethodDropdownOpen} onOpenChange={setPaymentMethodDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={paymentMethodDropdownOpen}
                      className="w-full justify-between"
                    >
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 mr-2" />
                        {formData.payment_method
                          ? availablePaymentMethods?.find((method) => method.provider_name === formData.payment_method)?.provider_name
                          : (t('selectPaymentMethod') || 'اختر طريقة الدفع')}
                      </div>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder={t('searchPaymentMethods') || 'البحث عن طرق الدفع'} />
                      <CommandList>
                        <CommandEmpty>
                          {paymentMethodsLoading ? (t('loading') || 'جاري التحميل...') : (t('noPaymentMethods') || 'لا توجد طرق دفع')}
                        </CommandEmpty>
                        <CommandGroup>
                          {(() => {
                            console.log('💳 Available payment methods count:', availablePaymentMethods?.length);
                            console.log('💳 Payment methods:', JSON.stringify(availablePaymentMethods, null, 2));
                            return availablePaymentMethods
                              .filter((method) => method.status === 'active') // Only show active payment methods
                              .map((method) => {
                              console.log('💳 Rendering method:', method.provider_name);
                              return (
                            <CommandItem
                              key={method.id}
                              value={method.provider_name}
                              onSelect={(currentValue) => {
                                console.log('💳 Selected:', currentValue, method);
                                handleInputChange('payment_method', method.provider_name);
                                handleInputChange('provider_name', method.provider_name);
                                setPaymentMethodDropdownOpen(false);
                              }}
                              className="flex items-center"
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  formData.payment_method === method.provider_name ? 'opacity-100' : 'opacity-0'
                                }`}
                              />
                              {method.image_url && (
                                <img 
                                  src={method.image_url} 
                                  alt={method.provider_name}
                                  className="w-8 h-8 object-contain mr-2"
                                />
                              )}
                              <span>{method.provider_name}</span>
                            </CommandItem>
                              );
                            });
                          })()}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Coupon Code */}
              <div className="space-y-2">
                <Label htmlFor="couponCode">{t('couponCode')}</Label>
                <div className="flex items-center space-x-2">
                  <Percent className="h-4 w-4" />
                  <Input
                    id="couponCode"
                    value={formData.couponCode}
                    onChange={(e) => handleInputChange('couponCode', e.target.value)}
                    placeholder={t('enterCouponCode')}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Special Requests */}
            <div className="space-y-2">
              <Label htmlFor="special_requests">{t('specialRequests')}</Label>
              <Textarea
                id="special_requests"
                value={formData.special_requests}
                onChange={(e) => handleInputChange('special_requests', e.target.value)}
                placeholder={t('enterSpecialRequests')}
                rows={3}
              />
            </div>

            {/* Payment Summary */}
            {paymentSummary.total_amount > 0 && (
              <Card className="border-2 border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    {t('paymentSummary') || 'ملخص الدفع'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">{t('totalAmount') || 'المبلغ الإجمالي'}:</span>
                    <SaudiRiyal amount={paymentSummary.total_amount} variant="new" className="text-lg font-semibold" />
                  </div>
                  
                  {paymentSummary.coupon_discount > 0 && (
                    <div className="flex justify-between items-center py-2 border-b text-green-600">
                      <span>{t('couponDiscount') || 'خصم الكوبون'}:</span>
                      <div className="flex items-center gap-1">
                        <span>-</span>
                        <SaudiRiyal amount={paymentSummary.coupon_discount} variant="new" />
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="font-medium">{t('totalAmount') || 'المبلغ الإجمالي'}:</span>
                    <SaudiRiyal amount={paymentSummary.total_amount} variant="new" className="text-lg font-bold text-primary" />
                  </div>
                  
                  <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md mt-4">
                    <span className="font-medium">💡 {t('note') || 'ملاحظة'}:</span> {t('noDepositNote') || 'Full payment is required at booking time. No deposit needed.'}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  loading ||
                  !selectedPropertyId ||
                  !formData.check_in ||
                  !formData.check_out ||
                  (!isNewUser && !selectedUserId) ||
                  (isNewUser && !newUserPhone.trim())
                }
              >
                {loading
                  ? (t('creating') || 'جاري الإنشاء...')
                  : (t('createBooking') || 'إنشاء حجز')}
              </Button>
            </div>
          </form>
    </div>
  );
}
